import { HotelState } from "@/lib/redux/features/hotel/type"
import { normalizeHotelApiDate } from "@/utils/hotel/normalizeHotelApiDate"
import { parseChildrenAgesQueryValues } from "@/utils/hotel/parseChildrenAgesFromQuery"
import { normalizeHotelGuestCounts } from "@/utils/hotel/hotelGuestCounts"

export type HotelApiSearchCriteria = {
  in?: string | null
  out?: string | null
  adt?: number | string | null
  child?: number | string | null
  room?: number | string | null
  loc?: number | string | null
  type?: string | null
  locale?: string | null
  children_ages?: Array<number | string> | null
  childrenAges?: Array<number | string> | null
  searchId?: number | string | null
}

type SearchParamsLike =
  | URLSearchParams
  | Record<string, string | string[] | undefined>
  | null
  | undefined

function getParam(searchParams: SearchParamsLike, key: string): string {
  if (!searchParams) return ""
  if (typeof (searchParams as URLSearchParams).get === "function") {
    return (searchParams as URLSearchParams).get(key)?.trim() || ""
  }
  const raw = (searchParams as Record<string, string | string[] | undefined>)[key]
  if (Array.isArray(raw)) return raw[0]?.trim() || ""
  return `${raw ?? ""}`.trim()
}

export function isAffiliateHotelRenew(searchParams: SearchParamsLike): boolean {
  const renew = getParam(searchParams, "renew")
  return renew === "true" || renew === "1"
}

export function hasHotelSearchDatesInParams(searchParams: SearchParamsLike): boolean {
  return !!(getParam(searchParams, "in") && getParam(searchParams, "out"))
}

/** Affiliate deeplink (`renew=true`) without dates — BE applies defaults; FE must not guess dates. */
export function shouldSkipDefaultHotelDates(searchParams: SearchParamsLike): boolean {
  return isAffiliateHotelRenew(searchParams) && !hasHotelSearchDatesInParams(searchParams)
}

function hasSearchCriteriaFields(source: Record<string, unknown>): boolean {
  return (
    source.in != null ||
    source.out != null ||
    source.adt != null ||
    source.child != null ||
    source.room != null
  )
}

/** Read echoed search criteria from hotel search/detail API payloads. */
export function pickHotelApiSearchCriteria(payload: unknown): HotelApiSearchCriteria | null {
  if (!payload || typeof payload !== "object") return null

  const raw = payload as Record<string, unknown>
  const candidates: Record<string, unknown>[] = [raw]

  if (raw.pagination && typeof raw.pagination === "object") {
    candidates.push(raw.pagination as Record<string, unknown>)
  }

  for (const source of candidates) {
    if (!hasSearchCriteriaFields(source)) continue
    return {
      in: source.in as string | undefined,
      out: source.out as string | undefined,
      adt: source.adt as number | string | undefined,
      child: source.child as number | string | undefined,
      room: source.room as number | string | undefined,
      loc: source.loc as number | string | undefined,
      type: source.type as string | undefined,
      locale: source.locale as string | undefined,
      children_ages: source.children_ages as Array<number | string> | undefined,
      childrenAges: source.childrenAges as Array<number | string> | undefined,
      searchId: source.searchId as number | string | undefined,
    }
  }

  return null
}

export function hydrateHotelFormSearchFromApi(
  formSearch: HotelState["formSearch"],
  criteria: HotelApiSearchCriteria,
): void {
  const checkIn = normalizeHotelApiDate(criteria.in)
  const checkOut = normalizeHotelApiDate(criteria.out)

  if (checkIn) {
    formSearch.checkInDate = checkIn
  }
  if (checkOut) {
    formSearch.checkOutDate = checkOut
  }

  if (criteria.adt != null && `${criteria.adt}` !== "") {
    formSearch.adults = parseInt(`${criteria.adt}`, 10) || 1
  }
  if (criteria.child != null && `${criteria.child}` !== "") {
    formSearch.children = parseInt(`${criteria.child}`, 10) || 0
  }
  if (criteria.room != null && `${criteria.room}` !== "") {
    formSearch.roomsNum = parseInt(`${criteria.room}`, 10) || 1
  }

  const ages = criteria.childrenAges ?? criteria.children_ages
  if (Array.isArray(ages)) {
    formSearch.childrenAges = parseChildrenAgesQueryValues(
      ages.map((age) => `${age}`),
    )
  }

  if (criteria.locale) {
    formSearch.locale = `${criteria.locale}`
  }

  if (criteria.loc != null && criteria.type) {
    const locId = parseInt(`${criteria.loc}`, 10)
    const searchType = `${criteria.type}`
    const locationPatch = {
      id: locId,
      _id: `${locId}`,
      searchType,
    }

    formSearch.location = {
      ...formSearch.location,
      ...locationPatch,
    } as any

    formSearch.currentLocation = {
      ...formSearch.currentLocation,
      ...locationPatch,
    } as any
  }

  normalizeHotelGuestCounts(formSearch)
}
